<template>
	<div>
		<base-header class="pb-6 bg-light">
			<b-row align-v="center" class="py-4">
				<b-col cols="12" lg="6">
					<h6 class="h2 d-inline-block mb-0 text-dark">eSIM Search Inventory</h6>
				</b-col>
				<b-col cols="12" lg="6" class="text-right">
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</b-col>
			</b-row>

			<div v-if="noAccess">
				<b-card>
					<b-card-header>
						<p class="h2 mb-2">
							<b-icon-exclamation-triangle-fill variant="danger"></b-icon-exclamation-triangle-fill
							>&nbsp;Restricted
						</p>
					</b-card-header>
					<b-card-body>Access is Restricted</b-card-body>
				</b-card>
			</div>

			<div v-else>
				<b-row>
					<b-col xl="12">
						<b-card no-body>
							<b-card-body>
								<h4>
									<span v-b-tooltip.hover.topright="'Custom Filter: ' + JSON.stringify(filter)"
										>Filters ({{ filterSize }} active)</span
									>
								</h4>
								{{ resultsFound }}
								<b-dropdown
									class="float-right mr-2"
									variant="primary"
									toggle-class="text-decoration-none"
                  menu-class="card-body p-0 m-0"
								>
									<template #button-content> Export {{ totalRows }} records </template>
									<b-dropdown-item @click="exportData('csv')"> Export as CSV </b-dropdown-item>
									<b-dropdown-item @click="exportData('xls')">
										Export as XLS (Excel)
									</b-dropdown-item>
								</b-dropdown>
							</b-card-body>
						</b-card>
					</b-col>
				</b-row>

        <b-row>
          <b-col xl="6">
            <b-card no-body>
              <b-card-body>
                <div class="input-group">
                  <b-input-group>
                    <template #prepend>
                      <b-input-group-text :style="ps">ICCID</b-input-group-text>
                    </template>
                    <b-form-input
                        id="iccid"
                        data-cy="e-sim-iccid-input"
                        type="text"
                        v-model="iccid"
                        aria-describedby="iccid-feedback"
                        :state="iccidValidation"
                        @change="filtersChanged"
                    ></b-form-input>
                    <b-form-invalid-feedback id="iccid-feedback">
                      Invalid ICCID
                    </b-form-invalid-feedback>
                  </b-input-group>
                </div>

								<!--              <div class="input-group">-->
								<!--                <b-input-group>-->
								<!--                  <template #prepend>-->
								<!--                    <b-input-group-text :style="ps">IMSI</b-input-group-text>-->
								<!--                  </template>-->
								<!--                  <b-form-input id="imsi" type="number" v-model="imsi" @change="filtersChanged"></b-form-input>-->
								<!--                </b-input-group>-->
								<!--              </div>-->

								<div class="input-group">
									<b-input-group>
										<template #prepend>
											<b-input-group-text :style="ps">EID</b-input-group-text>
										</template>
										<b-form-input
											id="eid"
											type="text"
											v-model="eid"
											@change="filtersChanged"
										></b-form-input>
									</b-input-group>
								</div>

								<div class="input-group">
									<b-input-group>
										<template #prepend>
											<b-input-group-text :style="ps">Free text</b-input-group-text>
										</template>
										<b-form-input
											id="freetext"
											type="text"
											v-model="freetext"
											:state="freetextValidation"
											aria-describedby="freetext-feedback"
											@change="filtersChanged"
										></b-form-input>
										<b-form-invalid-feedback id="freetext-feedback">
											Invalid Free Text
										</b-form-invalid-feedback>
									</b-input-group>
								</div>
							</b-card-body>
						</b-card>
					</b-col>
					<b-col xl="6">
						<b-card no-body>
							<b-card-body>
								<div class="input-group">
									<b-input-group>
										<template #prepend>
											<b-input-group-text :style="ps">Carrier</b-input-group-text>
										</template>
										<b-form-select
											id="carrier"
											v-model="carrier"
											:options="carrier_options"
											@change="filtersChanged"
										></b-form-select>
									</b-input-group>
								</div>

								<div class="input-group">
									<b-input-group>
										<template #prepend>
											<b-input-group-text :style="ps">Status</b-input-group-text>
										</template>
										<b-form-select
											id="state"
											v-model="state"
											:options="state_options"
											@change="filtersChanged"
										></b-form-select>
									</b-input-group>
								</div>
							</b-card-body>
						</b-card>
					</b-col>
				</b-row>

				<b-row>
					<b-col xl="12">
						<b-card no-body>
							<b-card-body>
								<div class="grey-sideborder">
									<b-table
										head-row-variant="primary"
										id="inventoryTable"
										class="primary-table w-auto"
										style
										sticky-header="true"
										:items="submitLoading ? emptyData : items"
										:fields="fields"
										:sort-by.sync="sortBy"
										:sort-desc.sync="sortDesc"
										@sort-changed="sortingChanged"
										:no-provider-sorting="true"
										show-empty
										responsive
										striped
										hover
										outlined
									>
										<template #head(actions)="scope">
											<div class="text-left">
												<base-button
													type="warning"
													size="sm"
													v-on:click="inventorySearch"
													title="Refresh Results"
													v-b-tooltip.hover
												>
													<i class="fa fa-recycle"></i>
												</base-button>
											</div>
										</template>

										<template #cell(actions)="row">
											<span class="nowrap">
												<base-button
													:disabled="
														row.item['profileStatus'] === 'Unavailable' ||
														(row.item['profileStatus'] !== 'Available' &&
															row.item['profileStatus'] !== 'Released')
													"
													:hidden="isReadOnly"
													@click="handleClickEvent('order', row)"
													size="sm"
													type="success"
													title="Activate an eSIM"
													v-b-tooltip.hover
												>
													<b-icon icon="cloud-upload"></b-icon>
												</base-button>
												<base-button
													:disabled="row.item['profileStatus'] !== 'Released'"
													@click="handleClickEvent('qrcode', row)"
													size="sm"
													type="warning"
													title="Display QR Code for eSIM"
													v-b-tooltip.hover
												>
													<i class="fa fa-qrcode"></i>
												</base-button>
												<base-button
													:disabled="
														row.item['profileStatus'] === 'Unavailable' ||
														row.item['profileStatus'] === 'Available' ||
														row.item['profileStatus'] === 'Installed'
													"
													:hidden="isReadOnly"
													@click="handleClickEvent('cancel', row)"
													size="sm"
													type="danger"
													title="Reset eSIM to Available"
													v-b-tooltip.hover
												>
													<b-icon icon="x-circle"></b-icon>
												</base-button>
											</span>
										</template>

										<template #empty="scope">
											<div v-if="submitLoading" class="text-center">
												<b-icon icon="three-dots" animation="cylon" font-scale="4" />
											</div>
											<div v-else class="text-center">
												<b>{{ scope.emptyText }}</b>
											</div>
										</template>

										<template #emptyfiltered="scope">
											<h4>{{ scope.emptyFilteredText }}</h4>
										</template>

                    <template #cell(iccid)="row">
                      <span v-if="row.item['profileStatus'] !== 'Unavailable'">
                      <b-link
                          id="iccid"
                          :data-cy="`e-sim-iccid-${row.item['iccid']}`"
                          :to="'/eSIM/profile/:?iccid=' + row.item['iccid']"
                          title="view profile"
                          class="d-flex"
                      >
                        {{ row.value }} <i class="ni ni-curved-next ml-2 mt-1"></i>
                      </b-link>
                      </span>
                      <span 
                        v-else
                        :data-cy="`e-sim-iccid-${row.item['iccid']}`"
                      >
                        {{ row.value }}
                      </span>
                    </template>

										<template #cell(profileStatus)="row">
											<router-link :to="'/eSIM/profile/:?iccid=' + row.item['iccid']">
												<base-button
													size="xs"
													style="width: 100px"
													:type="getStateClass(row.item['profileStatus'])"
												>
													{{ row.item["profileStatus"] }}
												</base-button>
											</router-link>
										</template>
									</b-table>
								</div>
								<div>
									<br />
									<b-container class="text-xs">
										<b-row class="">
											<b-col xl="4" md="12" class="text-xl-left text-center mb-3">{{
												resultsFound
											}}</b-col>
											<b-col xl="4" md="12" class="text-center" v-if="totalRows > 0">
												<b-pagination
													v-model="currentPage"
													:total-rows="totalRows"
													:per-page="pageSize"
													:current-page="currentPage"
													aria-controls="inventoryTable"
													class="mb-3 justify-content-center"
												></b-pagination>
											</b-col>
											<b-col xl="4" md="12" class="text-xl-right text-center mb-3">{{
												resultsUpdated
											}}</b-col>
										</b-row>
									</b-container>
								</div>
							</b-card-body>
						</b-card>
					</b-col>
				</b-row>
			</div>
		</base-header>

		<b-modal id="qrCodeModal" size="md" centered hide-footer>
			<template #modal-title> QR Code</template>
			<div>
				<table class="p-5" style="color: black; background-color: white; border: 10px solid white">
					<tr>
						<td rowspan="4">
							<qrcode-vue :value="qrcodeSrc" size="100" level="H">
								{{ qrcodeSrc }}
							</qrcode-vue>
						</td>
						<td rowspan="4">&nbsp;</td>
						<td>ICCID</td>
						<td>
							<barcode :value="qr.iccid" format="code128" width="2" height="20">
								{{ qr.iccid }}
							</barcode>
						</td>
					</tr>
					<tr>
						<td>IMSI</td>
						<td>{{ qr.imsi }}</td>
					</tr>
					<tr>
						<td>SM-DP+</td>
						<td>{{ qr.smdpAddress }}</td>
					</tr>
					<tr>
						<td>Activation code</td>
						<td>{{ qr.matchingId }}</td>
					</tr>
				</table>
			</div>
		</b-modal>
	</div>
</template>
<script>
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import moment from "moment";
import commonService from "@/common/api-services/common";
import eSimDashboardService from "@/common/api-services/esim-dashboard-service";
import initService from "@/common/api-services/init-service";
import authService from "@/common/api-services/auth-service";
import ToastVue from "../../Components/Toast.vue";
import QrcodeVue from "qrcode.vue";
import {exportToCSV, exportToExcel} from "@/common/export-to-csv";

export default {
	components: {
		RouteBreadCrumb,
		ToastVue,
		QrcodeVue,
	},
	computed: {
		noAccess() {
			// Determine access based on menu link configuration
			let userRoles = initService.getUserRoles();
			let link = this.$store.getters["getLinkByLink"](this.$router.currentRoute.path);

			if (!link) {
				return true;
			} else {
				userRoles = userRoles.filter(role => link.roles.includes(role.toUpperCase()));
				return userRoles.length === 0;
			}
		},
		iccidValidation() {
			return this.iccid.length === 0 || this.iccid.length === 20;
		},
		freetextValidation() {
			if (this.freetext.match("/^[a-zA-Z0-9_]+([a-zA-Z0-9_]*[.-]?[a-zA-Z0-9_]*)*[a-zA-Z0-9_]+$/") === null) {
				return true;
			} else {
				return false;
			}
		},
	},
	data() {
		return {
			ps: "background-color: rgba(94,114,228) !important;color: #FFFFFF !important;",
			clickAction: "",
			source_iccid: "",
			target_iccid: "",
			transferTitle: "",
			target_reason: "",
			carrier_options: [],
			qr: [],
			qrcodeSrc: "",
			barcodeSrc: "",
			state_options: [
				{value: "", text: "All"},
				{value: "Available", text: "Available"},
				{value: "Allocated", text: "Allocated"},
				{value: "Installed", text: "Installed"},
				{value: "Released", text: "Released"},
				{value: "Downloaded", text: "Downloaded"},
				{value: "Linked", text: "Linked"},
				{value: "Failed", text: "Failed"},
			],
			submitLoading: false,
			showResults: false,
			resultsFound: "",
			resultsUpdated: "",
			sortBy: "iccid",
			sortDesc: false,
			pageSize: 10,
			currentPage: 1,
			filterSize: 0,
			currentSort: "",
			computedSort: "",
			totalRows: 0,
			totalPages: 0,
			fields: [
				{key: "actions", sortable: false},
				{key: "iccid", label: "ICCID", sortable: true, thClass: "primary-table"},
				{key: "imsi", label: "IMSI", sortable: true, thClass: "primary-table"},
				{key: "profileType", label: "Profile Type", sortable: true, thClass: "primary-table"},
				{key: "lastModified", label: "Last Active", sortable: true, thClass: "primary-table"},
				{key: "created", label: "Created", sortable: true, thClass: "primary-table"},
				{key: "profileStatus", label: "Profile Status", sortable: true, thClass: "primary-table"},
			],
			items: [],
			pageOptions: [5, 10, 20, 50, {value: Number.MAX_SAFE_INTEGER, text: "show all"}],
			iccid: "",
			imsi: "",
			eid: "",
			freetext: "",
			filter: "",
			state: "",
			carrier: "",
			sort: "",
			range: "",
			emptyData: [],

			//ACCESS ROLES
			customer: "",
			isReadOnly: true,
			isEditor: false,
			isAdministrator: false,
			isPortalAdmin: false,
		};
	},

	mounted: function () {
		document.title = "ESIM - Inventory";

		/** NOTE: The following needs to be in data:
     isReadOnly: true,
     isEditor: false,
     isAdministrator: false,
     isPortalAdmin: false,
     */

		//set the roles [isReadOnly, isEditor, isAdministrator, isPortalAdmin]
		this.isReadOnly = initService.getUserRoles().includes("ESIM_READ");
		this.isEditor = initService.getUserRoles().includes("ESIM_EDIT");
		this.isAdministrator =
			initService.getUserRoles().includes("PORTAL_ADMIN") ||
			initService.getUserRoles().includes("CARRIER_ADMIN") ||
			initService.getUserRoles().includes("ESIM_ADMIN");
		this.isPortalAdmin = initService.getPortalAdminRole();

		// //write to console.
		// console.log("PAGE:", document.title);
		// console.log("isReadOnly:", this.isReadOnly);
		// console.log("isEditor:", this.isEditor);
		// console.log("isAdministrator:", this.isAdministrator);
		// console.log("isPortalAdmin:", this.isPortalAdmin);

		//get the carrier role
		if (this.isAdministrator || this.isPortalAdmin) {
			this.customer = "";
			console.log("getPortalCarrier: [admin]" + this.customer);
		} else if (process.env.VUE_APP_CLIENT === "uetn") {
			this.customer = "mnshub";
		} else {
			this.customer = initService.getPortalCarrier();
		}

		//don't do anything unless the user has access
		if (!this.noAccess) {
			//only load on page load
			this.readCache();
			// load the profile types to filter by
			this.getProfileTypes();
			// // query inventory
			// this.inventorySearch().catch(error => {
			//   console.error(error);
			// });
		} else {
			this.$router.push("/restricted").catch(() => {});
		}
	},

	watch: {
		currentPage: {
			handler: function (value) {
				this.inventorySearch();
			},
		},
	},

	methods: {
		async exportData(format) {
			let fileName = `${this.carrier}-eSim-inventory-${moment().format("MM-DD-YYYY")}`;
			await eSimDashboardService
				.getInventoryAdvanced('["iccid", "asc"]', `[0,${this.totalRows}]`, this.filter)
				.then(res => {
					// console.log("exportData:", res.data);
					if (res.hasOwnProperty("data") && res.data.hasOwnProperty("profiles")) {
						let arr = [];
						let data = res.data.profiles;
						// console.log("exportData:", data);
						data.map(item => {
							// console.log("exportData:", item);
							let row = {
								ICCID: "",
								IMSI: "",
								"Profile Type": "",
								"Profile Type Version": "",
								Created: "",
								"First Installed": "",
								"Last Modified": "",
								Status: "",
								Classifier: "",
								"Profile Id": "",
								"Profile Owner": "",
								"Operator Id": "",
								"Matching Id": "",
								"SMDP Address": "",
								"Confirmation Code Provided": "",
								"Private Group Used": "",
							};

							if (item.hasOwnProperty("profileOwner")) row["Profile Owner"] = item.profileOwner;
							if (item.hasOwnProperty("iccid")) row["ICCID"] = item.iccid;
							if (item.hasOwnProperty("operatorId")) row["Operator Id"] = item.operatorId;
							if (item.hasOwnProperty("matchingId")) row["Matching Id"] = item.matchingId;
							if (item.hasOwnProperty("smdpAddress")) row["SMDP Address"] = item.smdpAddress;
							if (item.hasOwnProperty("profileType")) row["Profile Type"] = item.profileType;
							if (item.hasOwnProperty("profileStatus")) row["Status"] = item.profileStatus;
							if (item.hasOwnProperty("lastModified"))
								row["Last Modified"] = moment(new Date(item.lastModified)).format(
									"MMM DD, YYYY hh:mm:ss A"
								);
							if (item.hasOwnProperty("profileClassifier")) row["Classifier"] = item.profileClassifier;
							if (item.hasOwnProperty("firstInstalledDateTime"))
								row["First Installed"] = moment(new Date(item.firstInstalledDateTime)).format(
									"MMM DD, YYYY hh:mm:ss A"
								);
							if (item.hasOwnProperty("profileId")) row["Profile Id"] = item.profileId;
							if (item.hasOwnProperty("imsi")) row["IMSI"] = item.imsi;
							if (item.hasOwnProperty("created"))
								row["Created"] = moment(new Date(item.created)).format("MMM DD, YYYY hh:mm:ss A");
							if (item.hasOwnProperty("confirmationCodeProvided"))
								row["Confirmation Code Provided"] = item.confirmationCodeProvided;
							if (item.hasOwnProperty("profileTypeVersion"))
								row["Profile Type Version"] = item.profileTypeVersion;
							if (item.hasOwnProperty("privateGroupUsed"))
								row["Private Group Used"] = item.privateGroupUsed;

							arr.push(row);
						});
						// console.log("exportData:", arr);
						if (format === "xls") {
							exportToExcel(arr, fileName);
						} else {
							exportToCSV(arr, fileName);
						}
					}
				})
				.catch(error => {
					console.error(error);
					this.$bvToast.toast(error, {
						title: "Error",
						variant: "danger",
						solid: true,
						autoHideDelay: 5000,
						appendToast: true,
					});
				});
			// exportToCSV(this.items, fileName);
		},

		readCache() {
			//get the last saved filter criteria
			let search = localStorage.getItem(this.$route.name);
			//console.log("reading search:", search);
			if (search) {
				search = JSON.parse(search);
				if (search.iccid) this.iccid = search.iccid;
				if (search.imsi) this.imsi = search.imsi;
				if (search.eid) this.eid = search.eid;
				if (search.freetext) this.freetext = search.freetext;
				if (search.carrier) this.carrier = search.carrier;
				if (search.state) this.state = search.state;
				if (search.page) this.page = search.page;
				if (search.sort) this.sort = search.sort;
			}
		},

		writeCache() {
			//set to current filter criteria
			let search = {
				iccid: this.iccid,
				imsi: this.imsi,
				eid: this.eid,
				freetext: this.freetext,
				carrier: this.carrier,
				state: this.state,
				page: this.page,
				sort: this.sort,
			};
			//console.log("writing search:", search);
			localStorage.setItem(this.$route.name, JSON.stringify(search));
		},

		order(row) {
			let msg =
				"You are about to activate this profile. After this operation the profile should be in a Released state.";
			this.$bvModal
				.msgBoxConfirm(msg, {
					title: "Confirmation",
					size: "sm",
					buttonSize: "sm",
					okVariant: "danger",
					okTitle: "APPLY",
					cancelTitle: "CANCEL",
					footerClass: "p-2",
					hideHeaderClose: false,
					centered: true,
				})
				.then(value => {
					if (value === true) {
						let iccid = row.item["iccid"];
						if (iccid === "undefined" || iccid === "" || !iccid)
							throw Error("ICCID is missing from eSIM inventory");
						let profileType = row.item["profileType"];
						if (profileType === "undefined" || !profileType) profileType = "";
						let matchingId = row.item["matchingId"];

						eSimDashboardService.downloadConfirmOrder(iccid, "", profileType, "", "", "").then(res => {
							let status = String(res.data.header.functionExecutionStatus.status);
							authService.setActivityLog("ESIM:downloadConfirmOrder", status + ": " + this.iccid, 0);
							if (status !== "Failed") {
								this.$toast.success({
									component: ToastVue,
									props: {
										description: " ESIM Order successful " + this.iccid,
									},
								});
								setTimeout(() => {
									this.inventorySearch();
								}, 1000);
							} else {
								this.$toast.error({
									component: ToastVue,
									props: {
										description: " ESIM Order failed " + this.iccid,
									},
								});
							}
						});
					}
				})
				.catch(err => {
					authService.setActivityLog("ESIM:downloadConfirmOrder", JSON.stringify(err), 1);
					this.$toast.error({
						component: ToastVue,
						props: {
							description: " ESIM Order failed " + this.iccid,
						},
					});
				});
		},

		cancel(row) {
			let msg =
				"You are about to cancel this profile. After this operation the profile should be back in Available state.";
			this.$bvModal
				.msgBoxConfirm(msg, {
					title: "Confirmation",
					size: "sm",
					buttonSize: "sm",
					okVariant: "danger",
					okTitle: "APPLY",
					cancelTitle: "CANCEL",
					footerClass: "p-2",
					hideHeaderClose: false,
					centered: true,
				})
				.then(value => {
					if (value === true) {
						let iccid = row.item["iccid"];
						if (iccid === "undefined" || iccid === "" || !iccid)
							throw Error("ICCID is missing from eSIM inventory");
						let eid = row.item["eid"];
						if (eid === "undefined" || !eid) eid = "";
						let matchingId = row.item["matchingId"];
						if (matchingId === "undefined" || matchingId === "" || !matchingId)
							throw Error("matchingId is missing from eSIM inventory");

						eSimDashboardService.cancelOrder(iccid, eid, matchingId, "Available").then(res => {
							let status = String(res.data.header.functionExecutionStatus.status);
							authService.setActivityLog("ESIM:Cancel", status + ": " + this.iccid, 0);
							if (status !== "Failed") {
								this.$toast.success({
									component: ToastVue,
									props: {
										description: " ESIM Cancel order successful " + this.iccid,
									},
								});
								setTimeout(() => {
									this.inventorySearch();
								}, 1000);
							} else {
								this.$toast.error({
									component: ToastVue,
									props: {
										description: " ESIM Cancel order failed " + this.iccid,
									},
								});
							}
						});
					}
				})
				.catch(err => {
					authService.setActivityLog("ESIM:cancel order", JSON.stringify(err), 1);
					this.$toast.error({
						component: ToastVue,
						props: {
							description: " Cancel order failed " + this.iccid,
						},
					});
				});
		},

		showQrcode(row) {
			try {
				this.qr = row.item;
				this.qr["smdpAddress"] = "smdp.io";
				this.smdpAddress = "smdp.io";
				this.matchingId = this.qr["matchingId"];

				this.smdpAddress = "smdp.io";
				this.$bvModal.show("qrCodeModal");
				this.qrcodeSrc = "LPA:1$" + this.qr["smdpAddress"] + "$" + this.qr["matchingId"];
				this.barcodeSrc = this.iccid;
				//authService.setActivityLog('ESIM:showQrcode', 'show qrcode OK: ' + this.iccid, 0);
			} catch (e) {
				//authService.setActivityLog('ESIM:showQrcode', JSON.stringify(e), 1);
				this.showToasterMsg("[showQrcode]Error:" + e.message, true, "danger");
			}
		},

		async handleClickEvent(action, row) {
			//console.log(action,iccid);
			let result = ``,
				msg = ``;
			try {
				let iccid = row.item["iccid"];

				//console.log(`action: ${action} iccid: ${iccid}`);

				switch (action) {
					case "qrcode":
						//this.$router.push({path: `/esim/qrcode/:?iccid=${iccid}`});
						this.showQrcode(row);
						break;

					case "order":
						this.order(row);
						break;

					case "cancel":
						this.cancel(row);
						break;

					default:
						break;
				}
			} catch (e) {}
		},

		getProfileTypes() {
			let last = "";
			eSimDashboardService.getProfileTypes().then(res => {
				this.carrier_options = [];
				if (res && res.data && res.data.profileTypes) {
					res.data.profileTypes.forEach(item => {
						let val = item.profileType;
						let txt = item.profileType;
						let test = item.profileType.toLowerCase().replaceAll(" ", "").replace("_", "");
						//console.log('test:',test,' customer:',this.customer.toLowerCase());
						if (this.customer !== "") {
							//this customer is assigned a carrier value
							if (
								test.includes(this.customer.toLowerCase()) ||
								test.includes(this.customer.toLowerCase())
							) {
								this.carrier_options.push({value: val, text: txt});
								last = val;
								//console.log('match:',val,txt);
							}
						} else {
							//admin so give them all
							this.carrier_options.push({value: val, text: txt});
							last = val;
						}
					});
				}

				if (!this.carrier) {
					this.carrier = last;
				}

				this.submitLoading = false;
				this.inventorySearch();
			});
		},

		async filtersChanged() {
			this.currentPage = 1;
			await this.inventorySearch();
		},

		async eidSearch() {
			//console.log(this.eid);

			// already loading so skip
			if (this.submitLoading) {
				//console.log("skipping, current results loading...");
				return;
			}
			this.showResults = false;
			this.submitLoading = true;
			this.resultsFound = "";
			await eSimDashboardService
				.getEidInfo(this.eid)
				.then(res => {
					//console.log('getEidInfo:', res);
					if (!res || res.data.header.functionExecutionStatus.status === "Failed")
						throw new Error("No results.");

					if (res.data.profiles && res.data.profiles.length > 0) {
						this.totalPages = 1;
						this.totalRows = res.data.profiles.length;
					} else {
						throw new Error("No results.");
					}

					this.showResults = true;
					this.resultsFound = `${this.totalRows} ${this.totalRows === 1? "record found" : "records found"}, NOTE: only EID(eUICC) filter enabled.`;
					let d = [];

					let dt = Date.now();
					if (this.totalRows > 0 && res.data.profiles) {
						Object.keys(res.data.profiles).forEach(i => {
							try {
								let item = res.data.profiles[i];

								if (item.hasOwnProperty("imsi")) item.imsi = commonService.decodeImsi(item.imsi);

								if (item.hasOwnProperty("lastModified"))
									item.lastModified = commonService.strToDate(item.lastModified);

								if (item.hasOwnProperty("created"))
									item.created = commonService.strToDate(item.created);

								if (item.hasOwnProperty("firstInstalledDateTime"))
									item.created = commonService.strToDate(item.firstInstalledDateTime);
								d.push(item);
							} catch (e) {
								console.error(e);
							}
						});
						this.items = d;
					} else {
						this.items = null;
						this.totalRows = 0;
						this.resultsFound = "0 record(s) found";
					}
				})
				.catch(e => {
					console.error(e);
					this.items = null;
					this.totalRows = 0;
					this.resultsFound = "0 record(s) found.";
				});

			this.submitLoading = false;
			this.resultsUpdated = "Last Updated: " + moment().format("MM/DD/YYYY hh:mm:ss");
		},

		async inventorySearch() {
			if (this.eid !== "") return this.eidSearch();

			if (!this.iccidValidation || !this.freetextValidation) {
				return false;
			}

			// Create the inventory filters
			if (!this.searchEvent()) return;

			if (this.filter === '[{"filter":[]}]') {
				return;
			}

			// already loading so skip
			if (this.submitLoading) {
				//console.log("skipping, current results loading...");
				return;
			}

			this.showResults = false;
			this.submitLoading = true;
			this.resultsFound = "";
			//console.log(this.sort, this.range, this.filter);
			await eSimDashboardService
				.getInventoryAdvanced(this.sort, this.range, this.filter)
				.then(res => {
					//console.log('getInventoryAdvanced:', res);
					if (res.data.paging.total_pages) {
						this.totalPages = res.data.paging.total_pages;
						this.totalRows = res.data.paging.total;
					}

					this.showResults = true;
					this.resultsFound = `${this.totalRows} ${this.totalRows === 1? "record found" : "records found"}`;
					let d = [];

					let dt = Date.now();
					if (this.totalRows > 0 && res.data.profiles) {
						Object.keys(res.data.profiles).forEach(i => {
							try {
								let item = res.data.profiles[i];

								if (item.hasOwnProperty("imsi")) item.imsi = commonService.decodeImsi(item.imsi);

								if (item.hasOwnProperty("lastModified"))
									// item.lastModified = commonService.strToDate(item.lastModified);
									item.lastModified = moment(item.lastModified).format("MMM DD, YYYY hh:mm:ss A");

								if (item.hasOwnProperty("created"))
									// item.created = commonService.strToDate(item.created);
                  item.created = moment(item.created).format("MMM DD, YYYY hh:mm:ss A");

								if (item.hasOwnProperty("firstInstalledDateTime"))
									// item.created = commonService.strToDate(item.firstInstalledDateTime);
                  item.created = moment(item.firstInstalledDateTime).format("MMM DD, YYYY hh:mm:ss A");
								d.push(item);
							} catch (e) {
								console.error(e);
							}
						});
						this.items = d;
					} else {
						this.items = null;
						this.totalRows = 0;
						this.resultsFound = "0 records found";
					}
				})
				.catch(e => {
					console.error(e);
					this.items = null;
					this.totalRows = 0;
					this.resultsFound = "0 records found.";
				});
			this.submitLoading = false;
			this.resultsUpdated = "Last Updated: " + moment().format("MM/DD/YYYY hh:mm:ss");
		},

		async reloadPage() {
			this.items = null;
			this.totalRows = 0;
			this.currentPage = 1;
			await this.inventorySearch();
		},

		searchEvent() {
			//console.log('carrier set search event:', this.carrier);

			// Build out the new search filter
			let status = true;
			let json = "";

			// Update [page]
			if (this.currentPage && this.currentPage !== "" && this.currentPage > 1) {
				// Calculate range
				this.page = this.currentPage - 1;
				let start = this.page * this.pageSize;
				let end = start + (this.pageSize - 1);
				this.range = "[" + start + "," + end + "]";
			} else {
				this.page = 0;
				this.range = "[0," + (this.pageSize - 1) + "]";
			}

			// Update [sort]
			if (this.computedSort && this.computedSort !== "") {
				this.sort = this.computedSort;
			} else {
				this.sort = '["iccid","asc"]';
			}

			// Create the filters
			let filters = [];
			let f = [];

			if (this.freetext === "") {
				// Update [filters]  [iccid,profileType,state,freetext(all)]
				// if (this.iccid !== "") f.push({iccid: this.iccid});
				//
				// if (this.state !== "") f.push({state: this.state});
				//
				// if (this.carrier !== "") f.push({type: this.carrier});
				//
				// if (this.imsi !== "") f.push({imsi: this.imsi});
				//
				// if (this.eid !== "") f.push({eid: this.eid});
				// f.push(
				//     {
				//       iccid: this.iccid,
				//       state: this.state,
				//       type: this.carrier,
				//       imsi: this.imsi,
				//       eid: this.eid,
				//     }
				//   );

				let f0 = "[{";
				if (this.iccid !== "") f0 += '"iccid": "' + this.iccid + '",';
				if (this.state !== "") f0 += '"state": "' + this.state + '",';
				if (this.carrier !== "") f0 += '"type": "' + this.carrier + '",';
				if (this.imsi !== "") f0 += '"imsi": "' + this.imsi + '",';
				if (this.eid !== "") f0 += '"owner": "' + this.eid + '"';
				if (f0.length > 2) {
					f0 = f0.slice(0, -1); //remove trailing comma
				}
				f0 += "}]";
				f = JSON.parse(f0);

				//add to filters
				filters.push({filter: f});

				// Add OR filters for the freetext option
			} else {
				if (this.iccid !== "") {
					this.iccid = "";
					this.$toast.warning({
						component: ToastVue,
						props: {
							description:
								"When using the freetext option, you cannot have an iccid selected. The iccid has been cleared.",
						},
					});
				}

				let f0 = "[{";
				// f = [];
				// f.push({iccid: this.freetext});
				// f.push({type: this.carrier});
				f0 = "[{";
				f0 += '"iccid_like": "' + this.freetext + '",';
				f0 += '"type": "' + this.carrier + '",';
				if (this.state !== "") f0 += '"state": "' + this.state + '",';
				f0 = f0.slice(0, -1); //remove trailing comma
				f0 += "}]";
				f = JSON.parse(f0);
				filters.push({filter: f});

				// f = [];
				// f.push({type: this.freetext});
				// f.push({type: this.carrier});
				f0 = "[{";
				f0 += '"type_like": "' + this.freetext + '",';
				f0 += '"type": "' + this.carrier + '",';
				if (this.state !== "") f0 += '"state": "' + this.state + '",';
				f0 = f0.slice(0, -1); //remove trailing comma
				f0 += "}]";
				f = JSON.parse(f0);
				//filters.push({filter: f});

				// f = [];
				// f.push({state: this.freetext});
				// f.push({type: this.carrier});
				f0 = "[{";
				f0 += '"state_like": "' + this.freetext + '",';
				f0 += '"type": "' + this.carrier + '",';
				if (this.state !== "") f0 += '"state": "' + this.state + '",';
				f0 = f0.slice(0, -1); //remove trailing comma
				f0 += "}]";
				f = JSON.parse(f0);
				//filters.push({filter: f});

				//EID
				// f = [];
				// f.push({owner: this.freetext});
				// f.push({type: this.carrier});
				f0 = "[{";
				f0 += '"owner_like": "' + this.freetext + '",';
				f0 += '"type": "' + this.carrier + '",';
				if (this.state !== "") f0 += '"state": "' + this.state + '",';
				f0 = f0.slice(0, -1); //remove trailing comma
				f0 += "}]";
				f = JSON.parse(f0);
				//filters.push({filter: f});

				//IMSI
				// f = [];
				// f.push({type_imsi: this.freetext});
				// f.push({type: this.carrier});
				f0 = "[{";
				f0 += '"imsi_like": "' + this.freetext + '",';
				f0 += '"type": "' + this.carrier + '",';
				if (this.state !== "") f0 += '"state": "' + this.state + '",';
				f0 = f0.slice(0, -1); //remove trailing comma
				f0 += "}]";
				f = JSON.parse(f0);
				//filters.push({filter: f});
			}

			// Update the filter variable.
			this.filterSize = 0;
			if (filters.length > 0) {
				this.filter = JSON.stringify(filters);
				if (this.iccid !== "") this.filterSize += 1;
				if (this.freetext !== "") this.filterSize += 1;
				if (this.carrier !== "") this.filterSize += 1;
				if (this.status !== "") this.filterSize += 1;
			} else {
				status = false; //there were no filters
			}

			//console.log("filters: ", this.filter);

			//store new search
			this.writeCache();

			return status;
		},

		async sortingChanged(ctx) {
			//console.log(ctx.sortDesc, "sort direction");
			if (ctx.sortBy) {
				if (ctx.sortBy === "profileStatus") ctx.sortBy = "state";
				else if (ctx.sortBy === "profileType") ctx.sortBy = "type";
				else if (ctx.sortBy === "lastModified") ctx.sortBy = "updated";
				//console.log('ctxSortby:',ctx.sortBy)

				if (ctx.sortDesc) {
					this.computedSort = '["' + ctx.sortBy + '","desc"]';
				} else {
					this.computedSort = '["' + ctx.sortBy + '","asc"]';
				}
			} else {
				this.computedSort = '["iccid","asc"]';
			}
			await this.inventorySearch();
		},

		getStateClass(state) {
			return eSimDashboardService.getStateClass(state);
		},
	},
};
</script>